import {
  Button,
  Modal,
  Text,
  ModalCloseButton,
  ModalFooter,
  ModalContent,
  ModalOverlay,
  Heading,
  ModalBody,
  Flex,
  Avatar,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { selectSponsorshipFullName, selectSponsorshipPhotoUri } from '../../../../store/selectors/sponsorship'
import { selectFullName, selectCustomerPhotoUri, selectCustomerType } from '../../../../store/selectors/customerProfile'
import { useState } from 'react'
import * as Sentry from '@sentry/browser'
import { Listing, MessageTopicKey } from '../../../../api/gqlaxy/gql/generated/graphql'
import { useSendListingMessage } from '../../../../hooks/listings/useSendListingMessage'
import { SEND_PREQUAL_REQUEST } from '../../../../actions/actionTypes'
import { ClientsTrackingClient } from '../../../../quartermaster/src'
import { TrackingEventConstants } from '../../../../constants/tracking'
import { useSendMessage } from '../../../../hooks/useSendMessage'

export const MSG = defineMessages({
  fail: {
    id: 'ListingDetails.PrequalModal.fail',
    defaultMessage: 'Pre-qualification request failed to send.'
  },
  success: {
    id: 'ListingDetails.PrequalModal.success',
    defaultMessage: 'Pre-qualification request sent!'
  },
  confirm: {
    id: 'ListingDetails.PrequalModal.confirm',
    defaultMessage: 'Yes, get pre-qualified'
  },
  cancel: {
    id: 'ListingDetails.PrequalModal.cancel',
    defaultMessage: 'Cancel'
  },
  disclaimer: {
    id: 'ListingDetails.PrequalModal.disclaimer',
    defaultMessage:
      "This notifies that you're interested in getting pre-qualified. There's no obligation to move forward."
  },
  description: {
    id: 'ListingDetails.PrequalModal.description',
    defaultMessage:
      "It's a fast process that helps you understand your true affordability and can give you better negotiation power during the buying process"
  },
  reaHeading: {
    id: 'ListingDetails.PrequalModal.reaHeading',
    defaultMessage: '{name} is a trusted lending partner. Would you like them to pre-qualify you?'
  },
  loHeading: {
    id: 'ListingDetails.PrequalModal.loHeading',
    defaultMessage: 'Want {name} to pre-qualify you for a loan?'
  }
})
interface PrequalModalProps {
  isOpen: boolean
  listing?: Listing
  handleClose: () => void
  uiContext: string
  customPrequalMessage?: string
}

export const PrequalModal = ({ isOpen, listing, handleClose, uiContext, customPrequalMessage }: PrequalModalProps) => {
  const sponsorName = useSelector(selectSponsorshipFullName)
  const sponsorPhoto = useSelector(selectSponsorshipPhotoUri)
  const customerName = useSelector(selectFullName)
  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const customerType = useSelector(selectCustomerType)
  const toast = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const intl = useIntl()
  const dispatch = useDispatch()

  const { mutateAsync: sendListingMessage } = useSendListingMessage()
  const { mutateAsync: sendMessage } = useSendMessage()

  const handleSubmit = async () => {
    setIsLoading(true)

    dispatch({ type: SEND_PREQUAL_REQUEST })
    ClientsTrackingClient?.clientRequestedInfo({
      request_type: TrackingEventConstants.CLIENT_REQUESTED_PREQUAL,
      customer_type: customerType,
      ui_context: uiContext
    })

    try {
      if (listing) {
        await sendListingMessage({
          input: {
            message: 'I want to get pre-qualified for this home.',
            topicKey: MessageTopicKey.ListingsPrequalRequest,
            listingId: listing?.id
          }
        })
      } else {
        await sendMessage({
          input: {
            message: customPrequalMessage || 'I want to get pre-qualified',
            topicKey: MessageTopicKey.ListingsPrequalRequest
          }
        })
      }
      toast({
        title: intl.formatMessage(MSG.success),
        status: 'success'
      })
    } catch (err: any) {
      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: err.message || '',
          requestData: err.config?.data || {},
          requestErrors: Object.values(err.response?.data?.errors) || []
        }
      })
      toast({
        title: intl.formatMessage(MSG.fail),
        status: 'error'
      })
    } finally {
      return setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction={'row'} mt={10} justifyContent={'center'}>
            <Avatar height={100} width={100} src={customerPhoto} />
            {sponsorPhoto && <Avatar height={100} width={100} src={sponsorPhoto} ml={-5} />}
          </Flex>
          <Heading textAlign='center' as='h2' mt={10}>
            {sponsorName
              ? intl.formatMessage(MSG.reaHeading, { name: sponsorName })
              : intl.formatMessage(MSG.loHeading, { name: customerName })}
          </Heading>
          <Text mb={20} size={'sm'} textAlign='center' mt={4}>
            {intl.formatMessage(MSG.description)}
          </Text>
        </ModalBody>
        <ModalFooter justifyContent={'center'} flexDirection={'column'}>
          <Text mb={2} size={'3xs'} color='neutral.600'>
            {intl.formatMessage(MSG.disclaimer)}
          </Text>
          <Button onClick={handleSubmit} isLoading={isLoading} width={'100%'} variant={'solid'} colorScheme='primary'>
            {intl.formatMessage(MSG.confirm)}
          </Button>
          <Button onClick={handleClose} mt={2} width={'100%'} variant={'ghost'} textColor='neutral.600'>
            {intl.formatMessage(MSG.cancel)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
